/* eslint-disable prefer-template */
/* eslint-disable react/no-unused-prop-types -- props are forwarded whole to `entry()` */
import React, { useEffect } from 'react';
import { SerializedOutput } from '@fiverr-private/layout-component';
import { startOneTrustAnalytics, OneTrust as OneTrustComponent } from '@fiverr-private/onetrust';
import { stats } from '@fiverr-private/obs';
import { shouldApplyGDPR } from '../utils/shouldApplyGDPR';

import './style.scss';

declare global {
    interface Window {
        /** undefined on pages where GTM is disabled */
        dataLayer: any;
        Fiverr: any;
        OptanonWrapper: any;
    }

    const Fiverr: any;
}

export interface EntryProps extends SerializedOutput {
    locale: string;
    country: string;
    isCookiePolicyPage: boolean;
    /** V2-specific flag */
    isThirdPartyEnabled: boolean;
}

/** This code was copy-pasted from different onetrust-related GTM tags. */
const entry = (props: EntryProps) => {
    const { country, isCookiePolicyPage, isThirdPartyEnabled } = props;
    const isGDPRSubject = shouldApplyGDPR(country);

    startOneTrustAnalytics();

    if (!isCookiePolicyPage && !isThirdPartyEnabled && isGDPRSubject) {
        // see original code here https://tagmanager.google.com/#/container/accounts/113083/containers/127856/workspaces/1002092/tags/951

        window.OptanonWrapper = () => {
            const event = customEvent('onetrustloaded', undefined);

            window.dispatchEvent(event);

            function customEvent(name, detail) {
                if (typeof CustomEvent === 'function') {
                    return new CustomEvent(name, { detail });
                }

                const event = document.createEvent('CustomEvent');

                event.initCustomEvent(name, true, true, detail);

                return event;
            }
        };
    }

    if (isCookiePolicyPage) {
        // see original code here https://tagmanager.google.com/#/container/accounts/113083/containers/127856/workspaces/1002092/tags/3657

        const contentDiv = document.getElementById('cookie-policy-content');
        const sdkDiv = document.createElement('div');

        sdkDiv.setAttribute('id', 'ot-sdk-cookie-policy');
        sdkDiv.setAttribute('class', 'max-width-container');

        if (contentDiv) {
            contentDiv.appendChild(sdkDiv);
        }
    }

    if (!isGDPRSubject) {
        // see original code here https://tagmanager.google.com/#/container/accounts/113083/containers/127856/workspaces/1002092/tags/4175

        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'OneTrustLoaded',
                OnetrustActiveGroups: ',C0001,C0002,C0004,',
            });
            window.dataLayer.push({
                event: 'OneTrustGroupsUpdated',
                OnetrustActiveGroups: ',C0001,C0002,C0004,',
            });
        }
    }

    if (isThirdPartyEnabled && !isCookiePolicyPage && isGDPRSubject) {
        // see original code here https://tagmanager.google.com/#/container/accounts/113083/containers/127856/workspaces/1002092/tags/952

        const categoriesMap = {
            C0001: 'Strictly Necessary Cookies',
            C0002: 'Performance Cookies',
            C0003: 'Functional Cookies',
            C0004: 'Targeting Cookies',
        };

        const date = new Date();
        date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
        const expires = '; expires=' + date.toUTCString();
        const OABCcookieName = 'OptanonAlertBoxClosed';
        const isSafari = navigator.userAgent.indexOf('Safari') > -1;

        function datediff(first, second) {
            return Math.round((second - first) / (1000 * 60 * 60 * 24));
        }

        function updateOTExpiration(closeDateStr) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

            const closedDaysAgo = datediff(new Date(closeDateStr), new Date());

            if (closedDaysAgo < 365) {
                const OABCcookie = getCookie(OABCcookieName);
                document.cookie = OABCcookieName + '=' + OABCcookie + expires + ';path=/; Samesite=Lax;';
            }
        }

        function safeAddListener(element, event, callback) {
            if (!element) {
                return;
            }

            try {
                element.addEventListener(event, callback);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.log('exception during adding event to function: ', callback.toString());
            }
        }

        function toArray(nodeList) {
            return Array.prototype.slice.call(nodeList);
        }

        function getCookie(name) {
            const value = '; ' + document.cookie;
            const parts = value.split('; ' + name + '=');

            if (parts.length >= 2) {
                return parts.pop()!.split(';').shift();
            }
        }

        function sendEvent(payload) {
            if (!window || !window.Fiverr || !window.Fiverr.sendClientEvents) {
                return;
            }

            payload.user = { id: Fiverr.models.user.userId };
            payload.uid = getCookie('u_guid');
            payload.page = {
                ctx_id: Fiverr.models.ps.pageCtxId,
            };

            window.Fiverr.sendClientEvents(payload);
        }

        function sendGrafanaEvent(name) {
            if (!window || !window.Fiverr || !window.Fiverr._logEventToGraphite || !name) {
                return;
            }

            Fiverr._logEventToGraphite({
                label: 'cookie-consent.banner.' + name,
            });
        }

        function once(func) {
            let isRunned = false;

            return function () {
                if (!isRunned) {
                    func();
                }

                isRunned = true;
            };
        }

        function defer(func) {
            return function () {
                setTimeout(func, 0);
            };
        }

        let currentCategory = null;

        const initDetailsEventsStatic = once(() => {
            const allowAllCheckbox = document.getElementById('select-all-hosts-groups-handler');
            const search = document.getElementById('vendor-search-handler');

            safeAddListener(allowAllCheckbox, 'click', () => {
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_settings.allow_all',
                    cookie_category: currentCategory,
                });
            });

            safeAddListener(search, 'input', (event) => {
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_settings.search',
                    cookie_category: currentCategory,
                    query: event.target.value,
                });
            });
        });

        const initDetailsEventsDynamic = defer(() => {
            const hostCheckboxs = toArray(document.querySelectorAll('.host-checkbox-handler'));
            const hostDetails = toArray(document.querySelectorAll('.host-box'));

            hostCheckboxs.forEach((hostEl) => {
                const host = hostEl.getAttribute('aria-label');

                safeAddListener(hostEl, 'click', (event) => {
                    event.stopPropagation();
                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_settings.allow',
                        cookie_category: currentCategory,
                        host,
                    });
                });
            });

            hostDetails.forEach((hostDetailsEl) => {
                safeAddListener(hostDetailsEl, 'click', () => {
                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_settings.view_cookies',
                        cookie_category: currentCategory,
                    });
                });
            });
        });

        const initSettingsEvents = once(() => {
            const close = document.getElementById('close-pc-btn-handler');
            const categories = toArray(document.querySelectorAll('.category-group .category-item'));
            const toggles = toArray(document.querySelectorAll('.category-switch-handler'));
            const moreInfo = document.querySelectorAll('.privacy-notice-link')[0];
            const details = toArray(document.querySelectorAll('.category-host-list-handler'));
            const confirm = document.querySelectorAll('.save-preference-btn-handler')[0];

            safeAddListener(close, 'click', () => {
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_settings.close',
                });
            });

            categories.forEach((categoryEl) => {
                const category = categoriesMap[categoryEl.getAttribute('data-optanongroupid')];

                safeAddListener(categoryEl, 'click', (event) => {
                    if (event.target.tagName.toLowerCase() === 'label') {
                        return;
                    }

                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_settings.select_category',
                        cookie_category: category,
                    });
                });
            });

            toggles.forEach((toggleEl) => {
                const category = categoriesMap[toggleEl.getAttribute('data-optanongroupid')];

                safeAddListener(toggleEl, 'click', (event) => {
                    event.stopPropagation();
                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_settings.allow_category',
                        cookie_category: category,
                    });
                });
            });

            safeAddListener(moreInfo, 'click', (event) => {
                event.stopPropagation();
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_settings.more_info',
                });
            });

            safeAddListener(confirm, 'click', (event) => {
                event.stopPropagation();
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_settings.confirm',
                });
            });

            details.forEach((detailsEl) => {
                const category = categoriesMap[detailsEl.getAttribute('data-parent-id')];

                safeAddListener(detailsEl, 'click', (event) => {
                    currentCategory = category;
                    initDetailsEventsStatic();
                    initDetailsEventsDynamic();

                    event.stopPropagation();
                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_settings.details',
                        cookie_category: category,
                    });
                });
            });
        });

        const initPopupEvents = once(() => {
            const bannerAcceptButton = document.getElementById('onetrust-accept-btn-handler');
            const pcAllowAllButton = document.getElementById('accept-recommended-btn-handler');
            const closeButton = document.getElementById('onetrust-close-btn-container');
            const settingsButton = document.getElementById('onetrust-pc-btn-handler');
            const OABCcookie = getCookie('OABCcookieName');

            if (!OABCcookie) {
                safeAddListener(bannerAcceptButton, 'click', () => {
                    sendGrafanaEvent('accept');
                    sendEvent({
                        group: 'user_notification',
                        type: 'cookies_popup.allow_all_btn',
                    });
                });
            }

            safeAddListener(pcAllowAllButton, 'click', () => {
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_popup.allow_all_preference',
                });
            });

            safeAddListener(closeButton, 'click', () => {
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_popup.close',
                });
            });

            safeAddListener(settingsButton, 'click', () => {
                initSettingsEvents();

                sendGrafanaEvent('settings-click');
                sendEvent({
                    group: 'user_notification',
                    type: 'cookies_popup.open_preference',
                });
            });
        });

        function initOneTrustAnalytics() {
            const isBannerClosed = getCookie('OptanonAlertBoxClosed');
            const bannerEl = document.getElementById('onetrust-banner-sdk');

            if (isBannerClosed || !bannerEl) {
                if (isSafari) {
                    updateOTExpiration(isBannerClosed);
                }

                return;
            }

            sendGrafanaEvent('show');
            sendEvent({
                group: 'cookies_popup_impression',
                type: 'cookies_popup.show$viewport',
            });

            initPopupEvents();
        }

        window.OptanonWrapper = () => {
            initOneTrustAnalytics();
        };
    }
};

const OneTrust = (props: EntryProps) => {
    const shouldRender = shouldApplyGDPR(props.country) || props.isCookiePolicyPage;

    useEffect(() => {
        entry(props);

        if (shouldRender) {
            stats.count('gtm_stats.onetrust', 'loaded');
        }
    }, [shouldRender]);

    return shouldRender ? <OneTrustComponent locale={props.locale} /> : null;
};

export default OneTrust;
